import { type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { Link } from 'react-router-dom'
import { IconChevronRight } from '@tabler/icons-react'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { cn } from '@/utils/cn'
import type { AssistantAnswer } from '../useAssistant'
import { AssistantChart } from './AssistantChart'
import { AssistantTable } from './AssistantTable'
import { KpiRow } from './KpiRow'
import { getSpecialComponent } from './specials/specials-registry'

const assistantPatientLinkClass = 'font-medium text-[#2563eb] underline underline-offset-2'

import { partitionArtifacts, sectionAnchorId, type ArtifactSectionOf } from './artifacts-view.utils'

type ArtifactsShape = NonNullable<AssistantAnswer['artifacts']>
type Kpi = NonNullable<ArtifactsShape['kpis']>[number]
type Chart = NonNullable<ArtifactsShape['charts']>[number]
type Table = NonNullable<ArtifactsShape['tables']>[number]

type ArtifactSection = ArtifactSectionOf<Kpi, Chart, Table>

// Collapsible section (native <details>, expanded by default).
const ArtifactGroupSection = ({
  section,
  children,
}: {
  section: ArtifactSection
  children: ReactNode
}) => (
  <details
    open
    id={sectionAnchorId(section.label)}
    data-section-root={section.root}
    className="group/section rounded-xl border border-[#e2e8f0] bg-white shadow-sm"
  >
    <summary className="flex cursor-pointer items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold text-slate-700 select-none [&::-webkit-details-marker]:hidden">
      <IconChevronRight
        size={16}
        className="shrink-0 text-slate-400 transition-transform group-open/section:rotate-90"
      />
      {section.label}
    </summary>
    <div className="flex flex-col gap-3 border-t border-[#e2e8f0] px-3.5 py-3">{children}</div>
  </details>
)

// A section's contents in the standard kind order: KPIs, charts, tables.
const SectionArtifacts = ({ section }: { section: ArtifactSection }) => (
  <>
    {section.kpis.length > 0 && <KpiRow kpis={section.kpis} />}
    {section.charts.map((chart, index) => (
      <AssistantChart key={`${chart.title}-${chart.type}-${index}`} chart={chart} />
    ))}
    {section.tables.map((table, index) => (
      <AssistantTable key={`${table.title}-${index}`} table={table} />
    ))}
  </>
)

type ArtifactsViewProps = {
  artifacts: AssistantAnswer['artifacts']
  // Drives the answer bubble's styling and gates the structured artifacts (they
  // only accompany a completed turn). Defaults to 'complete' for tool results,
  // which carry no turn status.
  status?: AssistantAnswer['status']
  // When provided, render `followUpSuggestions` as clickable chips.
  onFollowUp?: (text: string) => void
  // Hide `followUpSuggestions` entirely — for non-conversational surfaces (e.g.
  // the Clinical Summary overview tab) where there is no chat to follow up in.
  showSuggestions?: boolean
}

// Single renderer for a turn's / tool result's artifacts: the answer prose, KPIs,
// charts, tables, and specials, in that order. Shared by the assistant bubble and
// the tool-call bubble so both render identically. Returns a fragment — the caller
// supplies the surrounding flex column (and its gap), matching the assistant
// bubble's existing layout.
export const ArtifactsView = ({
  artifacts,
  status = 'complete',
  onFollowUp,
  showSuggestions = true,
}: ArtifactsViewProps) => {
  const isError = status === 'error'
  const isCancelled = status === 'cancelled'
  const isComplete = status === 'complete'

  // Structured artifacts and follow-ups only accompany a completed turn.
  const kpis = isComplete ? artifacts?.kpis : undefined
  const charts = isComplete ? artifacts?.charts : undefined
  const tables = isComplete ? artifacts?.tables : undefined
  const specials = isComplete ? artifacts?.specials : undefined
  const followUps = isComplete && showSuggestions ? artifacts?.followUpSuggestions : undefined

  // Show the answer whenever there's prose. For a turn that ended without any —
  // a cancellation or an error — fall back to an explicit status message so the
  // bubble communicates what happened instead of rendering empty (or vanishing).
  // A completed turn with no prose (e.g. charts only) renders no answer bubble.
  const answer = artifacts?.answer ?? ''
  const bodyText =
    answer.trim().length > 0
      ? answer
      : isCancelled
        ? 'Response cancelled.'
        : isError
          ? 'Something went wrong.'
          : ''
  const showAnswer = bodyText.length > 0

  return (
    <>
      {showAnswer && (
        <div
          className={cn(
            'rounded-2xl rounded-bl-sm border px-3.5 py-2 text-sm shadow-sm',
            isError && 'border-red-200 bg-red-50 text-red-900',
            isCancelled && 'border-slate-200 bg-slate-100 text-slate-600 italic',
            !isError && !isCancelled && 'border-[#e2e8f0] bg-white text-slate-800',
          )}
        >
          <div
            className={cn(
              'prose prose-sm max-w-none leading-relaxed',
              'prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0',
              // Force visible list markers: Tailwind Preflight resets `ul { list-style: none }`
              // and the typography plugin's `:where()` rules are too low-specificity to win,
              // so bulleted/numbered lists render without markers without these.
              '[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
              'prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5',
              'prose-code:before:content-none prose-code:after:content-none',
              'prose-a:text-[#2563eb] hover:prose-a:underline',
              isError && 'prose-p:text-red-900',
              isCancelled && 'prose-p:text-slate-600',
              !isError && !isCancelled && 'prose-p:text-slate-800',
            )}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                a: ({ href, children }) =>
                  href?.startsWith('/patients/') ? (
                    <Link to={href} className={assistantPatientLinkClass}>
                      {children}
                    </Link>
                  ) : (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
              }}
            >
              {bodyText}
            </ReactMarkdown>
          </div>
        </div>
      )}
      {(() => {
        const { sections, ungrouped } = partitionArtifacts({ kpis, charts, tables })
        return (
          <>
            {ungrouped.kpis.length > 0 && <KpiRow kpis={ungrouped.kpis} />}
            {ungrouped.charts.map((chart, index) => (
              <AssistantChart key={`${chart.title}-${chart.type}-${index}`} chart={chart} />
            ))}
            {ungrouped.tables.map((table, index) => (
              <AssistantTable key={`${table.title}-${index}`} table={table} />
            ))}
            {sections.map((section) => (
              <ArtifactGroupSection key={section.label} section={section}>
                <SectionArtifacts section={section} />
              </ArtifactGroupSection>
            ))}
          </>
        )
      })()}
      {specials?.map((special, index) => {
        // Unknown special types render nothing — the registry is the contract.
        const SpecialView = getSpecialComponent(special.type)
        return SpecialView ? (
          <SpecialView key={`${special.type}-${index}`} special={special} />
        ) : null
      })}
      {followUps && followUps.length > 0 && onFollowUp && (
        <div className="flex flex-wrap gap-1.5">
          {followUps.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onFollowUp(suggestion)}
              className="rounded-full border border-[#e2e8f0] bg-white px-3 py-1 text-xs text-[#2563eb] transition hover:border-[#bfdbfe] hover:bg-[#eff6ff]"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
